import React, { useState } from 'react';
import { Form, Input, Button, Space, Modal, message, Switch, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { AdvSet, AdvSetRequest } from '../../types/adv';
import { advSetService } from '../../services/advSetService';

interface AdvSetFormProps {
  set?: AdvSet;
  isVisible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AdvSetForm: React.FC<AdvSetFormProps> = ({
  set,
  isVisible,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);

  const isEditing = !!set;
  const title = isEditing 
    ? t('advertising.sets.form.edit_title') 
    : t('advertising.sets.form.create_title');

  const handleSubmit = async (values: AdvSetRequest) => {
    setIsLoading(true);
    try {
      if (isEditing && set) {
        await advSetService.updateAdvSet(set.id, values);
        message.success(t('advertising.sets.notifications.update_success'));
      } else {
        await advSetService.createAdvSet(values);
        message.success(t('advertising.sets.notifications.create_success'));
      }
      onSuccess();
      onClose();
      form.resetFields();
    } catch (error) {
      console.error('Error submitting advertisement set form:', error);
      message.error(t('advertising.sets.notifications.error', { message: (error as Error).message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Reset form when modal opens or set changes
  React.useEffect(() => {
    if (isVisible) {
      form.setFieldsValue({
        adv_set_name: set?.adv_set_name || '',
        adv_set_period: set?.adv_set_period ?? 15, // Default 15 minutes
        adv_set_active: set?.adv_set_active ?? false,
      });
    }
  }, [form, set, isVisible]);

  return (
    <Modal
      title={title}
      open={isVisible}
      onCancel={onClose}
      footer={null}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          adv_set_name: set?.adv_set_name || '',
          adv_set_period: set?.adv_set_period ?? 15, // Default 15 minutes
          adv_set_active: set?.adv_set_active ?? false,
        }}
      >
        <Form.Item
          name="adv_set_name"
          label={t('advertising.sets.form.name')}
          rules={[
            {
              required: true,
              message: t('advertising.sets.form.name_required'),
            },
          ]}
        >
          <Input placeholder={t('advertising.sets.form.name_placeholder')} />
        </Form.Item>

        {/* Hidden field to maintain the period value with default 15 */}
        <Form.Item
          name="adv_set_period"
          hidden={true}
          initialValue={15}
        >
          <InputNumber />
        </Form.Item>

        <Form.Item
          name="adv_set_active"
          label={t('advertising.sets.form.active')}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
            >
              {t('advertising.sets.form.submit')}
            </Button>
            <Button onClick={onClose}>
              {t('advertising.sets.form.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AdvSetForm; 